import { CookieConsentActionTypes, type ConsentType } from 'actions/cookie-consent';
import createReducer from 'utils/create-reducer';
import { setStorage, getStorage, removeStorage } from 'utils/storage';

export type CookieConsentState = {
  consentType: ConsentType;
  hasConsentBeenSet: boolean;
};

const CONSENT_COOKIE_NAME = 'hyrox_cookie_consent';
const CONSENT_COOKIE_EXPIRY_DAYS = 365; // 1 year

const getPersistedConsent = (): ConsentType => {
  const savedConsent = getStorage(CONSENT_COOKIE_NAME);
  if (savedConsent && (savedConsent === 'all' || savedConsent === 'essential')) {
    return savedConsent as ConsentType;
  }
  return null;
};

const persistConsent = (consentType: ConsentType): void => {
  const cookieOptions = {
    domain: window.location.hostname,
    secure: window.location.protocol === 'https:',
    sameSite: 'Strict' as const,
  };

  if (consentType) {
    setStorage(CONSENT_COOKIE_NAME, consentType, {
      ...cookieOptions,
      expires: CONSENT_COOKIE_EXPIRY_DAYS,
    });
  } else {
    removeStorage(CONSENT_COOKIE_NAME, cookieOptions);
  }
};

const persistedConsent = getPersistedConsent();

export const initialCookieConsentState: CookieConsentState = {
  consentType: persistedConsent,
  hasConsentBeenSet: persistedConsent !== null,
};

const handlers = {
  [CookieConsentActionTypes.SET_CONSENT]: (
    state: CookieConsentState,
    payload: { consentType: ConsentType },
  ): CookieConsentState => {
    persistConsent(payload.consentType);

    return {
      ...state,
      consentType: payload.consentType,
      hasConsentBeenSet: true,
    };
  },

  [CookieConsentActionTypes.RESET_CONSENT]: (): CookieConsentState => {
    persistConsent(null);
    return {
      consentType: null,
      hasConsentBeenSet: false,
    };
  },
};

export const cookieConsentReducer = createReducer<CookieConsentState>(handlers);
